import { useEffect, useState } from 'react'
import { useConexao } from '../dados/conexao.js'
import { observarSincronizacao, sincronizar, type EstadoSincronizacao } from '../dados/sincronizacao.js'

/// Card permanente de conexão.
///
/// A `BarraSincronizacao` é um alerta: aparece quando há algo errado ou pendente e
/// some quando está tudo certo. Silêncio é bom para não poluir a tela, mas é péssimo
/// para a pergunta que o inspetor faz antes de descer do andaime — "isto aqui já
/// subiu?". Este card responde sempre, inclusive quando a resposta é "sim, tudo
/// certo".

export function CartaoConexao() {
  const { estado: conexao, verificadoEm, verificar } = useConexao()
  const [sinc, setSinc] = useState<EstadoSincronizacao>({
    fase: 'parado',
    ultimaEm: null,
    pendentes: 0,
  })

  useEffect(() => observarSincronizacao(setSinc), [])

  const sincronizando = sinc.fase === 'sincronizando'
  const pendentes = sinc.fase === 'sincronizando' ? null : sinc.pendentes
  const ultima = sinc.fase === 'parado' ? sinc.ultimaEm : null

  const offline = conexao === 'offline'
  const verificando = conexao === 'verificando'

  const cor = offline
    ? 'border-slate-300 bg-slate-50'
    : verificando
      ? 'border-slate-200 bg-white'
      : pendentes && pendentes > 0
        ? 'border-moderada/30 bg-moderada/5'
        : 'border-leve/30 bg-leve/5'

  return (
    <div className={`cartao mb-4 flex items-center gap-3 border p-3 ${cor}`}>
      <Indicador estado={conexao} pulsando={sincronizando} />

      <div className="min-w-0 flex-1">
        <p className="font-semibold text-slate-800">
          {verificando ? 'Verificando conexão…' : offline ? 'Sem conexão' : 'Conectado'}
        </p>
        <p className="text-xs text-slate-600">{descrever({ offline, verificando, sincronizando, pendentes, ultima, sinc })}</p>
      </div>

      {!verificando && (
        <button
          onClick={() => {
            verificar()
            if (!offline) void sincronizar({ forcado: true })
          }}
          disabled={sincronizando}
          className="botao-neutro shrink-0 !min-h-0 !py-1.5 text-sm"
        >
          {offline ? 'Verificar' : 'Enviar agora'}
        </button>
      )}

      {verificadoEm && !verificando && (
        <span className="sr-only">
          Última verificação às {verificadoEm.toLocaleTimeString('pt-BR')}
        </span>
      )}
    </div>
  )
}

function descrever({
  offline,
  verificando,
  sincronizando,
  pendentes,
  ultima,
  sinc,
}: {
  offline: boolean
  verificando: boolean
  sincronizando: boolean
  pendentes: number | null
  ultima: string | null
  sinc: EstadoSincronizacao
}): string {
  if (verificando) return 'Perguntando ao servidor se ele responde.'

  if (offline) {
    const guardado =
      pendentes && pendentes > 0
        ? `${pendentes} ${pendentes === 1 ? 'inspeção guardada' : 'inspeções guardadas'} no aparelho`
        : 'Nada pendente'
    return `${guardado}. Tudo sobe sozinho quando o sinal voltar — pode continuar trabalhando.`
  }

  if (sincronizando) return `Enviando — ${sinc.fase === 'sincronizando' ? sinc.passo : ''}`
  if (sinc.fase === 'erro') return sinc.mensagem

  if (pendentes && pendentes > 0) {
    return `${pendentes} ${pendentes === 1 ? 'inspeção aguardando' : 'inspeções aguardando'} envio.`
  }

  return ultima
    ? `Tudo enviado. Última sincronização às ${new Date(ultima).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}.`
    : 'Tudo enviado.'
}

function Indicador({ estado, pulsando }: { estado: 'verificando' | 'online' | 'offline'; pulsando: boolean }) {
  const cor =
    estado === 'offline' ? 'bg-slate-400' : estado === 'verificando' ? 'bg-slate-300' : 'bg-leve'
  return (
    <span
      aria-hidden
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${
        estado === 'offline' ? 'bg-slate-200' : estado === 'verificando' ? 'bg-slate-100' : 'bg-leve/15'
      }`}
    >
      <span className={`h-3 w-3 rounded-full ${cor} ${pulsando ? 'animate-pulse' : ''}`} />
    </span>
  )
}
